import React, { useState, useMemo, useContext, useEffect } from 'react';
import './businessDrawer.css';

import { Skeleton, ZoidsRoot, If } from '@cleartax/zoids';

import Highligher from './highliter';

// styles
import {
  TableRow,
  GSTRow,
  GSTName,
  BranchRow,
  DrawerItem,
  SearchBox,
  StyledAvatar,
  GSTDrawItems,
  GSTColumn,
  GSTINNumber,
  BranchText,
  BlueImg,
  SearchField,
  CancelButton,
} from './styles';
// icons

// utils
import { searchQuery } from './utils';

// types
import { BusinessDetails, BusinessLevels } from '../../constants/schema';
import { BIZ_ICON, BLUE_TICK_MARK, WHITE } from '../../constants/urls';
import BottomDraw from '../bottomDraw/bottomDraw';
import { UserContext } from '../context/userContext';
import { track } from '../../analytics/analytics';
import { EventIDs } from '../../analytics/events';
import NoBusiness from '../layout/noBusiness/noBusiness';

// Metrics tracking
// import Metrics from '@shared/modules/metrics';
// import { EventActions } from '@shared/components/tracking/events';

const renderAvatar = (value: any, row: any, index: any) => {
  const initials = value
    ? value
        .split(' ')
        .map((x: any) => x[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    : undefined;

  if (row.level === BusinessLevels.PAN || row.level === BusinessLevels.GSTIN) {
    return (
      <StyledAvatar size="small">
        {initials}
      </StyledAvatar>
    );
  }
  return (
    <img
      src={BIZ_ICON}
      alt=""
      width="36px"
      height="36px"
      style={{ marginRight: '8px', marginLeft: '1px' }}
    />
  );
};

interface Props {
  closeBusinessDrawer: () => void;
  showBusinessDrawer: boolean;
  panDb: any;
  businessDataStore: any;
  selectOrg: (arg0: string, arg1: string) => void;
}

const Main: React.FC<Props> = ({
  showBusinessDrawer,
  closeBusinessDrawer,
  panDb,
  businessDataStore,
  selectOrg,
}) => {
  const showPanOnly = false;
  const loading = false;
  const { orgId, email } = useContext<Record<string, any>>(UserContext);
  const [query, setQuery] = useState('');
  const [orgID, setorgID] = useState(orgId || localStorage.getItem('ORGID'));

  // const totalGstns = useMemo(() => {
  //   let childs = 0;
  //   if (panDb) {
  //     panDb.forEach((id: any) => {
  //       const panDetails = businessDataStore[id];
  //       if (panDetails.childIds) {
  //         childs += panDetails.childIds.length;
  //       }
  //     });
  //   }
  //   return childs;
  // }, [panDb, businessDataStore]);
  useEffect(() => {
    if (orgId || localStorage.getItem('ORGID')) {
      setorgID(orgId || localStorage.getItem('ORGID'));
    }
  }, [orgId]);

  const businessData = useMemo(
    () => panDb.map((pan: any) => businessDataStore[pan]),
    [panDb, businessDataStore]
  );

  const buzData = useMemo(() => searchQuery(businessData, query), [
    businessData,
    query,
  ]);

  const handleItemClick = (e: any, item: any) => {
    e.preventDefault();
    if (!item.canAccess) {
      return;
    }
    selectOrg(item.id, item.level);
    const payload = {
      email,
    };
    track(EventIDs.CHOOSE_BUSINESS, payload);
    setQuery('');
    closeBusinessDrawer();
  };

  useEffect(() => {
    if (!showBusinessDrawer) {
      setQuery('');
    } else {
      const payload = {
        email,
      };
      track(EventIDs.BUSINESS_DRAW, payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBusinessDrawer]);

  const handleSearch = (e: any) => setQuery(e.target.value);

  const renderChild = (item: BusinessDetails) => {
    if (!item) {
      return null;
    }
    if (!item.__children.length) {
      return null;
    }

    return item.__children.map((child, index) => {
      switch (child.level) {
        case BusinessLevels.GSTIN:
          return (
            <GSTRow key={child.id}>
              {/* If showPanOnly is true then disable childs */}
              <DrawerItem
                disabled={showPanOnly || !child.canAccess}
                onClick={(e) => handleItemClick(e, child)}
                selected={orgID === child.id}
              >
                <GSTDrawItems>
                  {renderAvatar(child.entityName, child, index)}
                  <GSTColumn>
                    <GSTName>
                      <span
                        style={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          width: '100%',
                        }}
                      >
                        <Highligher q={query} text={child.entityName || ''} />
                      </span>
                    </GSTName>
                    {child.level === BusinessLevels.GSTIN && 
                    child.identifier && (
                      <GSTINNumber>
                        GSTIN: <Highligher q={query} text={child.identifier} />
                      </GSTINNumber>
                    )}
                  </GSTColumn>
                </GSTDrawItems>
                <BlueImg
                  alt=""
                  src={orgID === child.id ? BLUE_TICK_MARK : WHITE}
                />
              </DrawerItem>
              {renderChild(child)}
            </GSTRow>
          );
        default:
          return (
            <BranchRow key={child.id}>
              {/* If showPanOnly is true then disable childs */}
              <DrawerItem
                disabled={showPanOnly || !child.canAccess}
                onClick={(e) => handleItemClick(e, child)}
                selected={orgID === child.id}
              >
                <BranchText>
                  {renderAvatar(child.identifier, child, index)}
                  <span
                    style={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      width: '80%',
                    }}
                  >
                    <Highligher q={query} text={child.entityName || ''} />
                  </span>
                </BranchText>
                <BlueImg
                  alt=""
                  src={orgID === child.id ? BLUE_TICK_MARK : WHITE}
                />
              </DrawerItem>
            </BranchRow>
          );
      }
    });
  };

  //   useEffect(() => {
  //     if (!open) {
  //       setQuery('');
  //     }
  //   }, [open]);

  const closeKeyBoard = (e: any) => {
    if (e.keyCode === 13) {
      document.getElementById('target')?.blur();
    }
  };

  const closeBusinessDraw = () => {
    const payload = {
      email,
    };
    track(EventIDs.CLOSE_BUSINESS_DRAW, payload);
    closeBusinessDrawer();
  };

  return (
    <ZoidsRoot>
      <BottomDraw
        isOpen={showBusinessDrawer}
        drawHeight={540}
        logout={false}
        closeBottomDraw={closeBusinessDraw}
        title="Switch Business"
        content={
          <>
            {loading ? (
              <Skeleton />
            ) : (
              <>
                <If condition={panDb.length === 0}>
                  <NoBusiness />
                </If>
                <If condition={panDb.length > 0}>
                  <SearchBox>
                    <SearchField
                      id="target"
                      type="text"
                      placeholder="Search Business / GSTINs"
                      value={query}
                      onKeyDown={closeKeyBoard}
                      onChange={handleSearch}
                    />
                    {query && <CancelButton onClick={() => setQuery('')} />}
                  </SearchBox>
                </If>
                {buzData.map((item: any, index: any) => (
                  <div key={item.id}>
                    <TableRow>
                      <DrawerItem
                        id={item.id}
                        disabled={!item.canAccess}
                        onClick={(e) => handleItemClick(e, item)}
                        selected={orgID === item.id}
                      >
                        <GSTDrawItems>
                          {renderAvatar(item.entityName, item, index)}
                          <GSTColumn>
                            <GSTName>
                              <span
                                style={{
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  width: '100%',
                                }}
                              >
                                <Highligher
                                  q={query}
                                  text={item.entityName || ''}
                                />
                              </span>
                            </GSTName>
                            {item.level === BusinessLevels.PAN &&
                              item.identifier && (
                                <GSTINNumber>
                                  PAN:{' '}
                                  <Highligher
                                    q={query}
                                    text={item.identifier}
                                  />
                                </GSTINNumber>
                              )}
                          </GSTColumn>
                        </GSTDrawItems>
                        <BlueImg
                          alt=""
                          style={{ marginLeft: '1%' }}
                          src={orgID === item.id ? BLUE_TICK_MARK : WHITE}
                        />
                      </DrawerItem>
                      {renderChild(item)}
                    </TableRow>
                  </div>
                ))}
              </>
            )}
          </>
        }
      />
    </ZoidsRoot>
  );
};

export default Main;
